import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Cog, Plus, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI, controllingAPI } from '../../services/api';
import { toast } from 'sonner';
import { RecordActions } from '../../components/common/RecordActions';
import { RecordViewDialog } from '../../components/common/RecordViewDialog';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = ['MACHINE', 'LABOR', 'PROCESSING', 'ASSEMBLY', 'INSPECTION'];
const EMPTY = {
  work_center_id: '', description: '', plant_id: '', category: 'MACHINE',
  controlling_area_id: '', cost_center_id: '', capacity_per_day: 8,
  num_individual_capacities: 1, efficiency_pct: 100, status: 'ACTIVE',
};

const WorkCenters = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [plants, setPlants] = useState([]);
  const [areas, setAreas] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [wcRes, plantRes, areaRes] = await Promise.all([
        mfgAPI.getWorkCenters(),
        mfgAPI.getPlants(),
        controllingAPI.getControllingAreas(),
      ]);
      setRows(wcRes.data);
      setPlants(plantRes.data);
      setAreas(areaRes.data);
    } catch (e) {
      toast.error('Failed to load work centers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadCostCenters = async (areaId) => {
    if (!areaId) { setCostCenters([]); return; }
    try {
      const res = await controllingAPI.getCostCenters(areaId);
      setCostCenters(res.data || []);
    } catch { setCostCenters([]); }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setCostCenters([]); setOpen(true); };
  const openEdit = (wc) => {
    setEditing(wc);
    setForm({ ...EMPTY, ...wc, controlling_area_id: wc.controlling_area_id || '', cost_center_id: wc.cost_center_id || '' });
    loadCostCenters(wc.controlling_area_id);
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.work_center_id || !form.description || !form.plant_id) {
      toast.error('Work Center ID, Description and Plant are required');
      return;
    }
    const payload = {
      ...form,
      capacity_per_day: Number(form.capacity_per_day),
      num_individual_capacities: Number(form.num_individual_capacities),
      efficiency_pct: Number(form.efficiency_pct),
      controlling_area_id: form.controlling_area_id || null,
      cost_center_id: form.cost_center_id || null,
    };
    try {
      if (editing) {
        await mfgAPI.updateWorkCenter(editing.work_center_id, payload);
        toast.success('Work Center updated');
      } else {
        await mfgAPI.createWorkCenter(payload);
        toast.success('Work Center created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (wc) => {
    if (!window.confirm(`Delete work center ${wc.work_center_id}?`)) return;
    try {
      await mfgAPI.deleteWorkCenter(wc.work_center_id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };
  void remove;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="work-centers-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing')}><ChevronLeft className="h-5 w-5" /></Button>
            <div className="p-2 bg-orange-600 rounded-lg"><Cog className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Work Centers</h1>
              <p className="text-sm text-slate-500">Resources where operations are performed</p>
            </div>
          </div>
          {canWrite('work_center') && (
            <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="create-work-center-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Work Center
            </Button>
          )}
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold">Work Center</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Plant</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold">Cost Center</TableHead>
                  <TableHead className="font-semibold text-right">Capacity/Day</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No work centers yet</TableCell></TableRow>
                ) : rows.map((wc) => (
                  <TableRow key={wc.work_center_id} data-testid={`wc-row-${wc.work_center_id}`} className="hover:bg-orange-50/40">
                    <TableCell className="font-mono font-medium text-orange-600">{wc.work_center_id}</TableCell>
                    <TableCell>{wc.description}</TableCell>
                    <TableCell>{wc.plant_id}</TableCell>
                    <TableCell><Badge variant="outline">{wc.category}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{wc.cost_center_id || '—'}</TableCell>
                    <TableCell className="text-right">{wc.capacity_per_day} h</TableCell>
                    <TableCell>
                      <Badge className={wc.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-200 text-slate-600 border-0'}>{wc.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RecordActions
                        doctype="work_center"
                        idForTestId={wc.work_center_id}
                        onView={() => setViewItem(wc)}
                        onEdit={() => openEdit(wc)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Work Center</DialogTitle>
              <DialogDescription>Configure a production resource with capacity and cost-center assignment.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Work Center ID *</Label>
                <Input data-testid="wc-id-input" value={form.work_center_id} disabled={!!editing}
                  onChange={(e) => set('work_center_id', e.target.value.toUpperCase())} maxLength={10} />
              </div>
              <div>
                <Label>Description *</Label>
                <Input data-testid="wc-desc-input" value={form.description} onChange={(e) => set('description', e.target.value)} />
              </div>
              <div>
                <Label>Plant *</Label>
                <Select value={form.plant_id} onValueChange={(v) => set('plant_id', v)} disabled={!!editing}>
                  <SelectTrigger data-testid="wc-plant-select"><SelectValue placeholder="Select plant" /></SelectTrigger>
                  <SelectContent>
                    {plants.map((p) => <SelectItem key={p.plant_id} value={p.plant_id}>{p.plant_id} — {p.name || p.plant_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger data-testid="wc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Controlling Area</Label>
                <Select value={form.controlling_area_id || 'none'} onValueChange={(v) => { const val = v === 'none' ? '' : v; set('controlling_area_id', val); set('cost_center_id', ''); loadCostCenters(val); }}>
                  <SelectTrigger data-testid="wc-area-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {areas.map((a) => <SelectItem key={a.controlling_area_id} value={a.controlling_area_id}>{a.controlling_area_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cost Center</Label>
                <Select value={form.cost_center_id || 'none'} onValueChange={(v) => set('cost_center_id', v === 'none' ? '' : v)} disabled={!form.controlling_area_id}>
                  <SelectTrigger data-testid="wc-cc-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {costCenters.map((cc) => <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>{cc.cost_center_id} — {cc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Capacity / Day (hours)</Label>
                <Input type="number" data-testid="wc-capacity-input" value={form.capacity_per_day} onChange={(e) => set('capacity_per_day', e.target.value)} />
              </div>
              <div>
                <Label>Efficiency %</Label>
                <Input type="number" data-testid="wc-efficiency-input" value={form.efficiency_pct} onChange={(e) => set('efficiency_pct', e.target.value)} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set('status', v)}>
                  <SelectTrigger data-testid="wc-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="save-wc-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`Work Center ${viewItem?.work_center_id || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default WorkCenters;
